import {
  GET_ACCESSORY_BRANDS,
  GET_ACCESSORY_MODELS,
  GET_ACCESSORY_PRICE_DETAILS,
  ProductType,
  SchemaTypes,
} from '@allesauto/graphql';
import { type ApolloError, useLazyQuery, useQuery } from '@apollo/client';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import accessoryStandaloneToCartItemInput from '@/global/converters/accessoryStandaloneToCartItemInput';
import shipmentToShipmentCartItemInput from '@/global/converters/shipmentToShipmentCartItemInput';
import {
  IDataResponse,
  ILazyDataResponse,
} from '@/global/models/IDataResponse';
import { showCartNotification } from '@/store/redux/actions/app';

import {
  AccessoriesUrlFilters,
  AccessoriesUrlFiltersUpdate,
  AccessoriesUrlSorting,
  AccessoriesUrlSortingUpdate,
} from '../types';
import { filterParseParams } from '../utils/filterUtils';
import { sortingParseParams } from '../utils/sortingUtils';

/**
 * Get/set filters from/to the URL
 * @returns [current filters, callback to update filters]
 */
export function useUrlFilters(): [
  AccessoriesUrlFilters,
  (filters: AccessoriesUrlFiltersUpdate) => void
] {
  const router = useRouter();

  const filters = useMemo(() => filterParseParams(router.query), [router]);

  const setFilters = useCallback(
    (filters: AccessoriesUrlFiltersUpdate) => {
      const query = {
        ...router.query,
      };

      Object.entries(filters).forEach(([name, value]) => {
        if (value) {
          query[name] = value as any;
        } else {
          delete query[name];
        }
      });

      router.replace(
        {
          query,
        },
        {
          pathname: router.asPath.split('?')[0],
          query,
        },
        { shallow: true }
      );
    },
    [router]
  );

  return [filters, setFilters];
}

export function useUrlSorting(): [
  AccessoriesUrlSorting,
  (sorting: AccessoriesUrlSortingUpdate) => void
] {
  const router = useRouter();

  const sorting = useMemo(() => sortingParseParams(router.query), [router]);

  const setSorting = useCallback(
    (sorting: AccessoriesUrlSortingUpdate) => {
      const query = {
        ...router.query,
      };

      Object.entries(sorting).forEach(([name, value]) => {
        if (value) {
          query[name] = value as any;
        } else {
          delete query[name];
        }
      });

      router.replace(
        {
          query,
        },
        {
          pathname: router.asPath.split('?')[0],
          query,
        },
        { shallow: true }
      );
    },
    [router]
  );

  return [sorting, setSorting];
}

export const useAccessoriesBrands = (): IDataResponse<{
  brands: SchemaTypes.AccessoryBrand[];
}> => {
  const { data, loading, error } =
    useQuery<Pick<SchemaTypes.Query, 'accessoryBrands'>>(GET_ACCESSORY_BRANDS);

  return {
    data: { brands: data?.accessoryBrands ?? [] },
    loading,
    error,
  };
};

export const useAccessoriesModels = (): ILazyDataResponse<
  { models: SchemaTypes.AccessoryModelVariants[] },
  (
    distributorId: string
  ) => Promise<{ models: SchemaTypes.AccessoryModelVariants[] }>
> => {
  const [getModels, { data, loading, error }] =
    useLazyQuery<Pick<SchemaTypes.Query, 'accessoryModelsVariants'>>(
      GET_ACCESSORY_MODELS
    );
  return {
    executeCall: async (distributorId: string) => {
      const result = await getModels({
        variables: {
          distributorId,
        },
      });

      return { models: result.data?.accessoryModelsVariants ?? [] };
    },
    data: { models: data?.accessoryModelsVariants ?? [] },
    loading,
    error,
  };
};

export const useAccessoryPriceDetails = (): ILazyDataResponse<
  SchemaTypes.AccessoryPriceDetails,
  (
    dealerId?: string,
    productId?: string
  ) => Promise<SchemaTypes.AccessoryPriceDetails | null>
> => {
  const [getPriceDetails, { data, loading, error }] = useLazyQuery<
    Pick<SchemaTypes.Query, 'accessoryPriceDetails'>
  >(GET_ACCESSORY_PRICE_DETAILS);

  return {
    executeCall: async (dealerId?: string, productId?: string) => {
      if (!dealerId || !productId) {
        return null;
      }

      const result = await getPriceDetails({
        variables: { dealerId, productId },
      });

      return result.data?.accessoryPriceDetails ?? null;
    },
    data: data?.accessoryPriceDetails ?? null,
    loading,
    error,
  };
};

export const useAccessoryGroupedPriceDetails = (
  accessoryItems: SchemaTypes.CartItem[]
): IDataResponse<SchemaTypes.AccessoryPriceDetails[]> => {
  const [fetchPriceDetails] = useLazyQuery<
    Pick<SchemaTypes.Query, 'accessoryPriceDetails'>
  >(GET_ACCESSORY_PRICE_DETAILS);

  const [data, setData] = useState<SchemaTypes.AccessoryPriceDetails[] | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApolloError>();

  const accessoriesHash = accessoryItems
    .map((a) => (a.product as SchemaTypes.Accessory).productId)
    .join();

  useEffect(() => {
    try {
      const fetch = async () => {
        setData(null);
        setError(undefined);
        setLoading(true);

        const promises = accessoryItems.map((ait) => {
          const { dealerId, productId } = ait.product as SchemaTypes.Accessory;

          return fetchPriceDetails({
            variables: { dealerId, productId },
          });
        });

        const results = await Promise.all(promises);

        setLoading(false);

        if (results.every((r) => r.data && r.data.accessoryPriceDetails)) {
          setData(results.map((r) => r.data!.accessoryPriceDetails));
        } else {
          const invalidResponse = results.find((r) => r.error);

          setError(invalidResponse?.error);
        }
      };

      fetch();
    } catch (error) {
      setError(error as ApolloError);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessoriesHash, fetchPriceDetails]);

  return {
    data,
    loading,
    error,
  };
};

export const useAddAccessoryToCart = (
  addToCart: (
    items: SchemaTypes.CartItemInput[]
  ) => Promise<SchemaTypes.Cart | null>,
  updateCart: (
    deleteSKUs: string[],
    addItemInputs: SchemaTypes.CartItemInput[]
  ) => Promise<void>,
  makeName?: string | Maybe<string> | undefined
) => {
  const dispatch = useDispatch();

  return useCallback(
    async (
      dealer?: SchemaTypes.Dealer,
      dealerProductId?: string,
      shipment?: SchemaTypes.Shipment
    ) => {
      if (
        !dealer ||
        (!dealer.accessoryDealerId &&
          (!dealer.accessoriesProviders ||
            !dealer.accessoriesProviders[0]?.providerId)) ||
        !dealerProductId ||
        !shipment
      ) {
        return;
      }

      try {
        const providerIdOrAccessoryDealerId = dealer.accessoryDealerId
          ? dealer.accessoryDealerId
          : dealer.accessoriesProviders?.find((ap) => ap.makeName === makeName)
              ?.providerId ?? null;

        if (!providerIdOrAccessoryDealerId) {
          return;
        }

        const accessoryInput = accessoryStandaloneToCartItemInput(
          dealerProductId,
          providerIdOrAccessoryDealerId,
          dealer.id
        );

        const cart = await addToCart([accessoryInput]);

        // trying to find the accessory cart item we just added in the shopping cart
        const accessoryCartItem = cart?.items.find(
          (it) =>
            it.productType === ProductType.AccessoryStandAlone &&
            (it.product as SchemaTypes.Accessory).productId === dealerProductId
        );

        if (accessoryCartItem) {
          // then we need to add a shipment method for it, based on the option selected on the 2nd step of the overlay
          // trying to find the shipment cart item for the accessory
          const shipmentCartItem = cart?.items.find(
            (it) =>
              it.productType === ProductType.Shipment &&
              it.relatedProductSku === accessoryCartItem.sku
          );

          if (!shipmentCartItem) {
            // if we don't have a shipment yet - just add it
            const shipmentInput = shipmentToShipmentCartItemInput(
              shipment.sku,
              accessoryCartItem,
              dealer.id,
              undefined,
              dealer.id
            );

            await addToCart([shipmentInput]);
          } else if (
            (shipmentCartItem.product as SchemaTypes.Shipment).sku !==
            shipment.sku
          ) {
            // if we have a shipment, check if it has the same method (it means the same SKU)
            // if yes - we don't need to do anything, if not - we need to update the shipment cart item
            const shipmentInput = shipmentToShipmentCartItemInput(
              shipment.sku,
              accessoryCartItem,
              dealer.id,
              undefined,
              dealer.id
            );

            await updateCart([shipmentCartItem.sku], [shipmentInput]);
          }

          setTimeout(() => {
            dispatch(showCartNotification());
          }, 1000);
        }
      } catch (e) {
        // the error is handled by the apollo errorHandler
      }
    },
    [addToCart, dispatch, makeName, updateCart]
  );
};
